import { useCallback, useSyncExternalStore } from 'react';

const STORAGE_KEY = 'workflow-favorites';

// Module-level store so every useFavorites() instance (e.g. the home favorites
// section and the WorkflowBrowser stars) stays in sync live, and survives across
// the app via localStorage.
const read = (): Set<string> => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? new Set<string>(JSON.parse(saved)) : new Set();
  } catch {
    return new Set();
  }
};

let current = read();
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((l) => l());

const subscribe = (l: () => void) => {
  listeners.add(l);
  // Keep in sync with other tabs.
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) {
      current = read();
      emit();
    }
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(l);
    window.removeEventListener('storage', onStorage);
  };
};

const getSnapshot = () => current;

const setFavorites = (next: Set<string>) => {
  current = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...next]));
  } catch {
    /* ignore quota / private-mode errors */
  }
  emit();
};

// Manual per-user favorites (workflow IDs), shared across all hook instances.
export function useFavorites() {
  const favorites = useSyncExternalStore(subscribe, getSnapshot);

  const toggleFavorite = useCallback((id: string) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setFavorites(next);
  }, []);

  const isFavorite = useCallback((id: string) => favorites.has(id), [favorites]);

  return { favorites, toggleFavorite, isFavorite };
}
